import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Gift, Copy, Users, Trophy, Sparkles, Share2, CheckCircle2, Clock } from "lucide-react";

export default function Referrals() {
  const [stats, setStats] = useState({ code: "", invites_count: 0, successful_count: 0, rewards_earned_days: 0 });
  const [invites, setInvites] = useState([]);
  const [board, setBoard] = useState([]);
  const [applyCode, setApplyCode] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [c, inv, lb] = await Promise.all([
        api.get("/referrals/my-code"),
        api.get("/referrals/my-invites"),
        api.get("/referrals/leaderboard"),
      ]);
      setStats(c.data);
      setInvites(inv.data);
      setBoard(lb.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const shareLink = () => `${window.location.origin}/login?ref=${stats.code}`;

  const copy = async (text, msg = "تم النسخ") => {
    try { await navigator.clipboard.writeText(text); toast.success(msg); }
    catch { toast.error("فشل النسخ"); }
  };

  const share = async () => {
    const url = shareLink();
    const text = `انضم إلى محاسبتي واحصل على خصم 20% على اشتراكك الأول باستخدام الكود: ${stats.code}`;
    if (navigator.share) {
      try { await navigator.share({ title: "محاسبتي", text, url }); }
      catch {}
    } else {
      copy(`${text}\n${url}`, "تم نسخ رسالة الدعوة");
    }
  };

  const apply = async () => {
    if (!applyCode.trim()) return toast.error("أدخل كوداً");
    try {
      const { data } = await api.post("/referrals/apply", { code: applyCode.trim().toUpperCase() });
      toast.success(data.message || "تم التطبيق");
      setApplyCode("");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const STATUS = {
    pending: { l: "بانتظار الاشتراك", c: "bg-amber-100 text-amber-700", Icon: Clock },
    converted: { l: "مشترك ✓", c: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
  };

  return (
    <div className="space-y-6" data-testid="referrals-page">
      <div>
        <h1 className="font-heading font-bold text-2xl flex items-center gap-2"><Gift className="w-6 h-6 text-primary" /> برنامج الإحالات</h1>
        <p className="text-sm text-muted-foreground mt-1">ادعُ أصدقاءك — يحصلون على خصم 20% وأنت تحصل على شهر مجاني عن كل اشتراك</p>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground"><Users className="w-4 h-4" /> عدد الدعوات</div>
          <div className="text-2xl font-bold mt-2" data-testid="stat-invites">{stats.invites_count}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground"><CheckCircle2 className="w-4 h-4" /> اشتراكات ناجحة</div>
          <div className="text-2xl font-bold mt-2 text-emerald-600" data-testid="stat-success">{stats.successful_count}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground"><Sparkles className="w-4 h-4" /> أيام مكتسبة</div>
          <div className="text-2xl font-bold mt-2 text-primary" data-testid="stat-days">{stats.rewards_earned_days}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground"><Trophy className="w-4 h-4" /> ترتيبك</div>
          <div className="text-2xl font-bold mt-2">
            {(() => {
              const idx = board.findIndex(b => b.user_name === stats.user_name);
              return idx >= 0 ? `#${idx + 1}` : "—";
            })()}
          </div>
        </CardContent></Card>
      </div>

      {/* Referral Code Card */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Gift className="w-5 h-5 text-primary" /> كود الإحالة الخاص بك</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="p-6 border-2 border-dashed border-primary/30 rounded-lg bg-primary/5 text-center">
            <div className="text-xs text-muted-foreground mb-2">شارك هذا الكود مع أصدقائك</div>
            <div className="text-4xl font-mono font-bold tracking-widest text-primary" data-testid="referral-code">{stats.code || "..."}</div>
            <div className="flex gap-2 justify-center mt-4 flex-wrap">
              <Button onClick={() => copy(stats.code)} variant="outline" size="sm" data-testid="copy-code-btn"><Copy className="w-4 h-4 me-1" /> نسخ الكود</Button>
              <Button onClick={() => copy(shareLink(), "تم نسخ الرابط")} variant="outline" size="sm" data-testid="copy-link-btn"><Copy className="w-4 h-4 me-1" /> نسخ الرابط</Button>
              <Button onClick={share} size="sm" data-testid="share-btn"><Share2 className="w-4 h-4 me-1" /> مشاركة</Button>
            </div>
          </div>

          <div className="grid gap-2 sm:grid-cols-3 text-xs">
            <div className="p-3 rounded border bg-card"><div className="font-bold mb-1">1. شارك الكود</div><div className="text-muted-foreground">أرسل الكود أو الرابط لأصدقائك</div></div>
            <div className="p-3 rounded border bg-card"><div className="font-bold mb-1">2. يحصلون على خصم</div><div className="text-muted-foreground">خصم 20% على اشتراكهم الأول</div></div>
            <div className="p-3 rounded border bg-card"><div className="font-bold mb-1">3. تحصل على مكافأة</div><div className="text-muted-foreground">شهر مجاني عن كل مشترك ناجح</div></div>
          </div>
        </CardContent>
      </Card>

      {/* Apply Code (for new users) */}
      {invites.length === 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">هل لديك كود دعوة؟</CardTitle></CardHeader>
          <CardContent className="flex gap-2">
            <Input value={applyCode} onChange={e => setApplyCode(e.target.value.toUpperCase())} placeholder="ABCD1234" className="max-w-xs font-mono" data-testid="apply-code-input" />
            <Button onClick={apply} data-testid="apply-code-btn">تطبيق الكود</Button>
          </CardContent>
        </Card>
      )}

      {/* My Invites */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="w-5 h-5" /> المدعوّون ({invites.length})</CardTitle></CardHeader>
        <CardContent>
          {invites.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">لم تدعُ أحداً بعد — ابدأ بمشاركة الكود!</div>
          ) : (
            <div className="border rounded overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b"><tr>
                  <th className="text-start p-2">الاسم</th>
                  <th className="text-start p-2">الإيميل</th>
                  <th className="text-center p-2">الحالة</th>
                  <th className="text-start p-2">التاريخ</th>
                </tr></thead>
                <tbody>{invites.map(inv => {
                  const s = STATUS[inv.status] || STATUS.pending;
                  const SIcon = s.Icon;
                  return (
                    <tr key={inv.id} className="border-b hover:bg-muted/30" data-testid={`invite-${inv.id}`}>
                      <td className="p-2 font-medium">{inv.invitee_name || "—"}</td>
                      <td className="p-2 text-muted-foreground">{inv.invitee_email}</td>
                      <td className="p-2 text-center"><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${s.c}`}><SIcon className="w-3 h-3" /> {s.l}</span></td>
                      <td className="p-2 text-xs font-mono text-muted-foreground">{new Date(inv.created_at).toLocaleDateString("ar-EG")}</td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Leaderboard */}
      {board.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" /> أفضل المُحيلين</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {board.slice(0, 10).map((b, i) => (
                <div key={i} className={`flex items-center justify-between p-2 rounded ${i === 0 ? "bg-amber-50 border border-amber-200" : "bg-muted/30"}`} data-testid={`board-${i}`}>
                  <div className="flex items-center gap-2">
                    <span className={`w-6 h-6 grid place-items-center rounded-full text-xs font-bold ${i === 0 ? "bg-amber-500 text-white" : i === 1 ? "bg-slate-400 text-white" : i === 2 ? "bg-amber-700 text-white" : "bg-muted text-foreground"}`}>{i + 1}</span>
                    <span className="text-sm font-medium">{b.user_name || "مجهول"}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-emerald-600 font-bold">{b.successful_count} مشترك</span>
                    <span className="text-primary">{b.rewards_earned_days} يوم</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
